import React, { useEffect, useRef, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItemButton,
    ListItemText, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { formatPhone, personName } from '../../voice/callFormat';
import { missedCallsApi, errorMessage } from './missedCallsApi';
import { voiceApi } from '../../voice/voiceApi';
import {
    MATCHED_NUMBER_LABEL, MIN_PHONE_DIGITS, uploadedDay, looksLikePhoneQuery, phoneSearchDigits, whoLines,
} from './missedCallFormat';

const SEARCH_DEBOUNCE_MS = 300;
const MIN_TERM = 2;

/**
 * Who this number belongs to, by hand (docs/MISSED_CALLS_PLAN.md §6.2): the import matches the number against
 * the students', parents' and teachers' numbers on file, and this is for when it found nobody — or the wrong
 * person. What is picked here wins over the lookup and over the calls, and it is what the row's 전화 icon
 * hands the softphone. 지정 해제 gives the row back to the automatic lookup.
 *
 * Two searches, one at a time: 이름·상담번호 (as it always was — digits mean a consultation number) and 전화번호 (all or
 * part of any number on file, 4+ digits). Switching keeps what was typed and searches again.
 */
const WhoDialog = ({ row, open, onClose, onSaved }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [q, setQ] = useState('');
    const [mode, setMode] = useState('name');   // 'name' | 'phone'
    const [results, setResults] = useState({ students: [], teachers: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [pendingStudent, setPendingStudent] = useState(null);
    const [extraStudents, setExtraStudents] = useState([]);
    const [addingMore, setAddingMore] = useState(false);
    const timer = useRef(null);
    const seq = useRef(0);

    useEffect(() => { if (open) { setQ(''); setMode('name'); setResults({ students: [], teachers: [] }); setError(null); setPendingStudent(null); setExtraStudents([]); setAddingMore(false); } }, [open]);

    useEffect(() => {
        if (timer.current) clearTimeout(timer.current);
        const term = q.trim();
        const ready = mode === 'phone' ? phoneSearchDigits(term).length >= MIN_PHONE_DIGITS : term.length >= MIN_TERM;
        if (!ready) { setResults({ students: [], teachers: [] }); setLoading(false); return undefined; }
        setLoading(true);
        const mine = ++seq.current;
        timer.current = setTimeout(async () => {
            try {
                const out = await missedCallsApi.searchContacts(term, mode);
                if (mine !== seq.current) return;
                setResults({ students: out.students || [], teachers: out.teachers || [] });
                setError(null);
            } catch (err) {
                if (mine === seq.current) setError(errorMessage(err));
            } finally {
                if (mine === seq.current) setLoading(false);
            }
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer.current);
    }, [q, mode]);

    if (!row) return null;

    const save = async (body, done, attachCall = false) => {
        setBusy(true);
        let shouldClose = false;
        try {
            const out = await missedCallsApi.setWho(row.id, body);
            onSaved(out.row);
            if (attachCall) {
                try {
                    await voiceApi.updateCall(row.connected_call.id, {
                        student_uuid: pendingStudent.uuid,
                        // This is the full list. Sending [] also clears stale extra filings.
                        also_student_uuids: extraStudents.map((student) => student.uuid),
                    });
                } catch (err) {
                    enqueueSnackbar(`Who를 지정했지만 통화에는 연결하지 못했습니다: ${errorMessage(err)}`, { variant: 'warning' });
                    shouldClose = true;
                    return;
                }
            }
            enqueueSnackbar(done, { variant: 'success' });
            shouldClose = true;
        } catch (err) {
            enqueueSnackbar(errorMessage(err), { variant: 'error' });
        } finally {
            setBusy(false);
            if (shouldClose) onClose();
        }
    };

    const manual = row.who?.source === 'manual';
    const term = q.trim();
    const phoneMode = mode === 'phone';
    const ready = phoneMode ? phoneSearchDigits(term).length >= MIN_PHONE_DIGITS : term.length >= MIN_TERM;
    const nothing = !loading && ready && !results.students.length && (addingMore || !results.teachers.length) && !error;
    const rowNumber = formatPhone(row.phone_number) || row.phone_number;
    // A number typed into the name search would be read as a consultation number: offer the phone search instead.
    const suggestPhone = !phoneMode && looksLikePhoneQuery(term);
    const selectedStudents = pendingStudent ? [pendingStudent, ...extraStudents] : [];
    const removedExtraStudents = (row.connected_call?.extra_students || [])
        .filter((student) => !extraStudents.some((selected) => selected.uuid === student.uuid));
    const studentBody = selectedStudents.length === 1
        ? { student_uuid: pendingStudent.uuid }
        : { student_uuids: selectedStudents.map((student) => student.uuid) };
    const chooseStudent = (student) => {
        if (!pendingStudent) setPendingStudent(student);
        else if (!selectedStudents.some((selected) => selected.uuid === student.uuid)) setExtraStudents((current) => [...current, student]);
        setAddingMore(false);
    };

    return (
        <>
        <Dialog open={open && (!pendingStudent || addingMore)} onClose={busy ? undefined : addingMore ? () => setAddingMore(false) : onClose} fullWidth maxWidth="xs" disableEnforceFocus>
            <DialogTitle sx={{ pb: 0.5 }}>
                Who
                <Typography variant="body2" color="text.secondary">
                    {uploadedDay(row)} #{row.daily_seq} · {formatPhone(row.phone_number) || row.phone_number}
                    {' · '}
                    {whoLines(row).map((l) => l.text).join(', ')}
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={mode}
                        onChange={(e, v) => v && setMode(v)}
                        disabled={busy}
                    >
                        <ToggleButton value="name" sx={{ textTransform: 'none', px: 1.5 }}>이름·상담번호</ToggleButton>
                        <ToggleButton value="phone" sx={{ textTransform: 'none', px: 1.5 }}>전화번호</ToggleButton>
                    </ToggleButtonGroup>
                    {phoneMode && row.phone_number && (
                        <Button size="small" sx={{ textTransform: 'none' }} disabled={busy} onClick={() => setQ(rowNumber)}>
                            이 행의 번호
                        </Button>
                    )}
                </Box>
                <TextField
                    key={mode}
                    autoFocus
                    fullWidth
                    size="small"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={phoneMode ? '(201) 555-0101 — 일부만 입력해도 됩니다' : '학생 이름 / 상담번호, 선생님 이름'}
                    inputProps={phoneMode ? { inputMode: 'tel' } : undefined}
                    disabled={busy}
                />
                {suggestPhone && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        전화번호로 찾으시나요?
                        <Button size="small" sx={{ textTransform: 'none', py: 0, minWidth: 0, ml: 0.5 }} onClick={() => setMode('phone')}>
                            전화번호로 검색
                        </Button>
                    </Typography>
                )}
                {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
                {loading && <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={22} /></Box>}
                {nothing && <Typography color="text.secondary" sx={{ py: 2 }}>찾는 사람이 없습니다.</Typography>}
                {!ready && !manual && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        {phoneMode ? `전화번호를 ${MIN_PHONE_DIGITS}자리 이상 입력하세요.` : '두 글자 이상 입력하세요.'}
                        {' '}지정하면 이 행의 Who와, 이 행에서 거는 전화가 그 사람으로 채워집니다.
                    </Typography>
                )}
                <List dense disablePadding>
                    {results.students.map((s) => (
                        <ListItemButton
                            key={`s-${s.id}`}
                            disabled={busy}
                            onClick={() => chooseStudent(s)}
                        >
                            <ListItemText
                                primary={`${personName(s)}${s.consultation_number ? ` #${s.consultation_number}` : ''}`}
                                secondary={[s.korean_name, (phoneMode && MATCHED_NUMBER_LABEL[s.matched]) || (s.korean_name ? null : '학생')].filter(Boolean).join(' · ')}
                            />
                        </ListItemButton>
                    ))}
                    {!addingMore && results.teachers.map((t) => (
                        <ListItemButton
                            key={`t-${t.id}`}
                            disabled={busy}
                            onClick={() => save({ teacher_uuid: t.uuid }, `${personName(t)}(으)로 지정했습니다`)}
                        >
                            <ListItemText primary={personName(t)} secondary={phoneMode ? MATCHED_NUMBER_LABEL.teacher : '선생님'} />
                        </ListItemButton>
                    ))}
                </List>
            </DialogContent>
            <DialogActions>
                {manual && (
                    <Button color="inherit" disabled={busy} onClick={() => save({}, '자동 인식으로 되돌렸습니다')}>
                        지정 해제
                    </Button>
                )}
                <Box sx={{ flex: 1 }} />
                <Button onClick={addingMore ? () => setAddingMore(false) : onClose} disabled={busy}>{addingMore ? '돌아가기' : '닫기'}</Button>
            </DialogActions>
        </Dialog>
        <Dialog open={open && Boolean(pendingStudent) && !addingMore} onClose={busy ? undefined : () => setPendingStudent(null)} fullWidth maxWidth="xs">
            <DialogTitle>{row.connected_call?.id ? '통화를 학생에게 연결할까요?' : '학생 지정'}</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2">
                    {selectedStudents.map(personName).join(', ')} 학생을 Who로 지정합니다.
                    {row.connected_call?.id && ' 통화도 연결하면 요약이 각 학생 카드의 메모에 반영됩니다.'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {selectedStudents.map((student, index) => (
                        <Chip key={student.uuid} size="small" label={`${personName(student)}${index === 0 ? ' · 주 학생' : ''}`}
                            onDelete={busy ? undefined : () => {
                                if (index === 0) { setPendingStudent(null); setExtraStudents([]); }
                                else setExtraStudents((current) => current.filter((s) => s.uuid !== student.uuid));
                            }} />
                    ))}
                </Box>
                {row.connected_call?.memo?.id && row.connected_call.memo.student_uuid !== pendingStudent?.uuid && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        기존 학생의 통화 메모와 연결된 작업이 삭제됩니다.
                    </Alert>
                )}
                {removedExtraStudents.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        기존 추가 학생 {removedExtraStudents.map(personName).join(', ')}의 통화 메모와 연결된 작업이 있다면 삭제됩니다.
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button disabled={busy} onClick={() => setPendingStudent(null)}>취소</Button>
                {extraStudents.length < 10 && <Button disabled={busy} onClick={() => setAddingMore(true)}>학생 추가</Button>}
                <Box sx={{ flex: 1 }} />
                {row.connected_call?.id && <Button disabled={busy} onClick={() => save(studentBody, 'Who를 지정했습니다')}>Who만 지정</Button>}
                <Button disabled={busy} variant="contained" onClick={() => save(studentBody, row.connected_call?.id ? 'Who를 지정하고 통화를 학생 카드에 연결했습니다' : 'Who를 지정했습니다', Boolean(row.connected_call?.id))}>
                    {busy ? <CircularProgress size={18} /> : row.connected_call?.id ? '통화도 연결' : '지정'}
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
};

export default WhoDialog;
